import React, { useState } from 'react';
import { usePipelineMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { setCurrentStep, selectBranch, selectProjectPath, setPipelineId } from 'app/sharedSlice';
import { selectSnowflakeAccountLabel, selectSelectedDatabase } from 'app/snowflakeSlice';
import { selectFlow, selectSoleOutput } from '../../data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import OverviewBlock from 'components/OverviewBlock';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import SourceSelector from '../../data-product-builder/components/SourceSelector';
import SoleOutputVisualizer from '../components/SoleOutputVisualizer';

export interface DataProductSummaryStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function CiCdProjectSummaryStep(props: DataProductSummaryStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const flow = useAppSelector(selectFlow);
  const soleOutput = useAppSelector(selectSoleOutput);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);
  const selectedDatabase = useAppSelector(selectSelectedDatabase);

  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed trigger pipeline');

  const [pipeline] = usePipelineMutation();

  const onDryRun = (): void => {
    setLoading(true);
    setFailed(false);
    pipeline({ dataProductName: 'full', projectName: projectPath, branch, mergeMain: false, camelCase: false })
      .unwrap()
      .then((res: any) => {
        dispatch(setPipelineId(res));
        props.onContinue();
      })
      .catch((err) => {
        console.log(err);
        setFailed(true);
        setErrorMessage(err.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  console.log('Sole output:', soleOutput);

  /* eslint-disable */
  return (
    <StepWrapper
      title="The infrastructure-as-code configurations will be generated for the following selected objects"
      subtitle="Check the summary before publishing the data product branch to the DataOps project (repository)"
      onBack={() => props.onBack()}
      onContinue={() => props.onContinue()}
      isLoading={loading}
      isFailed={failed}
      errorMessage={errorMessage}
      isLastStep={true}
      customButtonLabel="Dry run"
      customButtonAction={() => onDryRun()}
      tooltip="Execute the CI/CD pipeline for the generated configuration in the FB database clone (i.e. Sandbox)"
    >
      <div className="px-6">
        <OverviewBlock
          dividerText="Dataset source"
          backgroundColor="bg-gray-50"
          showEditButton
          onEditClick={() => dispatch(setCurrentStep(1))}
        >
          <div className="px-6 text-sm text-gray-600">
            The metadata was imported from the following Snowflake account:{' '}
            <span className="font-semibold text-gray-800">{snowflakeAccountLabel}</span>
          </div>
        </OverviewBlock>

        {flow === 'snowflake' && (
          <OverviewBlock dividerText="Dataset schema" showEditButton onEditClick={() => dispatch(setCurrentStep(2))}>
            <div className="w-full flex flex-col items-center">
              <div className="px-6 text-sm text-gray-600 mb-4">The following objects were selected as sources</div>
              <SourceSelector
                mode="preview"
                objectTypes={['table', 'view', 'function', 'procedure', 'file_format', 'sequence', 'pipe']}
                databases={[selectedDatabase]}
              />
            </div>
          </OverviewBlock>
        )}

        {flow !== 'snowflake' && soleOutput && (
          <OverviewBlock dividerText="Infrastructure-as-code configuration" showEditButton={false}>
            <div className="w-full flex flex-col items-center">
              <div className="px-6 text-sm text-gray-600 mb-4">The following SOLE configuration will be generated</div>
              <SoleOutputVisualizer soleOutput={soleOutput} />
            </div>
          </OverviewBlock>
        )}

        <LoadingAndErrorSection isLoading={loading} isFailed={failed} errorMessage={errorMessage} hideLoading={true} />
      </div>
    </StepWrapper>
  );
}
